import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { GoogleLoginButton } from '../components/GoogleLoginButton';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (result.user?.role === 'super_admin') {
        navigate('/platform');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0D1B3D] text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="inline-flex bg-white rounded-xl px-4 py-3 shadow-lg">
            <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-10" />
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-5xl font-black tracking-tight leading-tight mb-4">
            Conectando lideranças,
            <br />
            <span className="text-emerald-400">multiplicando resultados.</span>
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            Plataforma de gestão de rede política via links personalizados, QR Codes e redirecionamento inteligente.
          </p>
        </div>
        <div className="relative z-10 text-xs text-slate-400">
          © MeAdd — todos os direitos reservados
        </div>

        {/* Decorative shapes */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 bg-slate-50 relative">
        <Link
          to="/"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium font-manrope transition-colors group"
          data-testid="login-back-btn"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Voltar
        </Link>
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="pt-8 pb-6 px-6 sm:px-8">
            <div className="lg:hidden mb-6 flex justify-center">
              <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-[4.25rem]" />
            </div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Bem-vindo de volta</h2>
              <p className="text-sm text-slate-500 mt-1">
                Acesse sua conta para gerenciar sua rede.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11"
                  data-testid="login-password-input"
                />
              </div>
              {error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full h-11 bg-[#0D1B3D] hover:bg-[#0D1B3D]/90 text-base font-semibold"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-manrope">ou</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <GoogleLoginButton label="Entrar com Google" testid="login-google-btn" />
            <p className="mt-4 text-xs text-slate-500 text-center font-manrope">
              Google é apenas para o owner da conta. Usuários internos continuam com e-mail/senha.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
